import {
  BulkSelectionScope,
  defaultsSeatStatusOptionFilter,
  MultiQuickFilterSelection,
  SelectionType as PlanSelectionType,
  SeatColorsTypeEnum,
  SeatStatusOptionFilter,
} from '@app/catalog-plan/models/catalog-plan.model';
import { BlockViewData } from '@core/models/catalog-plan.model';
import {
  CatalogPlanEventTypeEnumResponseDto,
  CatalogPlanSeatResponseDto,
  EventSessionMultiSelectionStatsResponseDto,
  EventSessionPlanBlockResponseDto,
  EventSessionPlanCategoryResponseDto,
  EventSessionPlanContingentImageResponseDto,
  EventSessionPlanContingentResponseDto,
  EventSessionPlanHeaderResponseDto,
  EventSessionPlanUnavailableSeatTypesResponseDto,
  EventSessionStatsResponseDto,
  EventSessionVenueInformationLegendResponseDto,
  FileResponse,
} from '@core/services/apiclient';
import { createReducer, on } from '@ngrx/store';
import * as CatalogPlanActions from '../actions/catalog-plan.actions';

export const catalogPlanFeatureKey = 'catalogPlan';

export interface State {
  eventSessionId: number;
  blockId: number;
  eventDetails: EventSessionPlanHeaderResponseDto;
  planBackground: FileResponse;
  planData: EventSessionPlanBlockResponseDto[];
  blockViewData: BlockViewData;
  allContingentImages: EventSessionPlanContingentImageResponseDto[];
  seatsStampVersion: string;
  isPlanLoading: boolean;
  selectionType: PlanSelectionType;
  planType: CatalogPlanEventTypeEnumResponseDto;
  seatSelection: CatalogPlanSeatResponseDto[];
  legend: EventSessionVenueInformationLegendResponseDto;
  contingentsFilterList: EventSessionPlanContingentResponseDto[];
  categoriesFilterList: EventSessionPlanCategoryResponseDto[];
  unavailableSeatTypesFilterList: EventSessionPlanUnavailableSeatTypesResponseDto[];
  contingentIdsFilterSelected: number[];
  categoryIdsFilterSelected: number[];
  unavailableSeatTypeIdsSelected: number[];
  seatStatusIdsFilterSelected: string[];
  seatStatusOptionFilterSelected: SeatStatusOptionFilter;
  seatColorsFilter: SeatColorsTypeEnum[];
  allCategories: EventSessionPlanCategoryResponseDto[];
  allContingents: EventSessionPlanContingentResponseDto[];
  allUnavailableSeats: EventSessionPlanUnavailableSeatTypesResponseDto[];
  blockCategories: EventSessionPlanCategoryResponseDto[];
  blockContingents: EventSessionPlanContingentResponseDto[];
  blockUnavailableSeats: EventSessionPlanUnavailableSeatTypesResponseDto[];
  selectedSeatChanges: {
    categoryId: number;
    contingentId: number;
    unavailabilityId: number;
  };
  multiQuickFilter: MultiQuickFilterSelection;
  viewSeatStatusStats: EventSessionStatsResponseDto;
  viewMultiSelectionStats: EventSessionMultiSelectionStatsResponseDto;
  planWideSeatStatusStats: EventSessionStatsResponseDto;
  filteredSeatStatusStats: EventSessionMultiSelectionStatsResponseDto;
  bulkSelectionStats: EventSessionMultiSelectionStatsResponseDto;
  // Perimetre gele de la selection en masse : voir BulkSelectionScope. Non nul exactement quand
  // bulkSelectionStats est non nul.
  bulkSelectionScope: BulkSelectionScope | null;
  // Stats filtrees du bloc courant pendant qu'une selection est gelee (cf. updateBlockScopedFilterStats).
  // Ecrite mais pas encore lue : cf. le garde-fou sur selectBlockScopedFilterStats.
  blockScopedFilterStats: EventSessionMultiSelectionStatsResponseDto;
  sourcePage?: 'dashboard' | 'catalog' | null;
}

export const initialState: State = {
  eventSessionId: null,
  blockId: null,
  eventDetails: null,
  planBackground: null,
  planData: null,
  blockViewData: null,
  allContingentImages: [],
  seatsStampVersion: null,
  isPlanLoading: false,
  selectionType: PlanSelectionType.Default,
  seatSelection: [],
  planType: null,
  legend: null,
  contingentsFilterList: [],
  categoriesFilterList: [],
  unavailableSeatTypesFilterList: [],
  contingentIdsFilterSelected: [],
  categoryIdsFilterSelected: [],
  unavailableSeatTypeIdsSelected: [],
  seatStatusIdsFilterSelected: [],
  seatStatusOptionFilterSelected: defaultsSeatStatusOptionFilter,
  seatColorsFilter: [SeatColorsTypeEnum.Categories, SeatColorsTypeEnum.Contingents],
  allCategories: [],
  allContingents: [],
  allUnavailableSeats: [],
  blockCategories: [],
  blockContingents: [],
  blockUnavailableSeats: [],
  selectedSeatChanges: {
    categoryId: null,
    contingentId: null,
    unavailabilityId: null,
  },
  multiQuickFilter: {
    selectedCategories: null,
    selectedContingents: null,
    selectedUnavailabilities: null,
    selectedStatuses: null,
    filterType: null,
  },
  viewSeatStatusStats: null,
  viewMultiSelectionStats: null,
  planWideSeatStatusStats: null,
  filteredSeatStatusStats: null,
  bulkSelectionStats: null,
  bulkSelectionScope: null,
  blockScopedFilterStats: null,
  sourcePage: null,
};

export const reducer = createReducer(
  initialState,
  on(CatalogPlanActions.resetPlan, (state) => ({
    ...initialState,
    sourcePage: state.sourcePage,
  })),
  on(CatalogPlanActions.initPlanView, (state, { eventSessionId, sourcePage }) => ({
    ...initialState,
    eventSessionId,
    sourcePage,
  })),
  on(CatalogPlanActions.setEventSessionId, (state, { eventSessionId }) => ({
    ...initialState,
    eventSessionId,
    sourcePage: state.sourcePage,
  })),

  on(
    CatalogPlanActions.loadTopViewPlanDataSuccess,
    (state, { eventDetails, planBackground, planData, allContingentImages }) => ({
      ...state,
      planBackground,
      planData,
      eventDetails,
      allContingentImages,
      isPlanLoading: false,
      blockViewData: null,
      seatSelection: [],
    }),
  ),
  on(CatalogPlanActions.loadTopViewPlanDataFailure, (state) => ({
    ...state,
    planBackground: null,
    planData: null,
    isPlanLoading: false,
  })),
  on(CatalogPlanActions.loadPlanData, (state, { blockId }) => {
    return {
      ...state,
      isPlanLoading: true,
      blockId,
    };
  }),
  on(CatalogPlanActions.setLegend, (state, { legend }) => ({
    ...state,
    legend,
  })),
  on(CatalogPlanActions.getSeatSearchResults, (state, { searchTerm }) => ({
    ...state,
    searchTerm,
  })),
  on(CatalogPlanActions.getSeatSearchResultsSuccess, (state, { results }) => ({
    ...state,
    searchResults: results,
  })),
  on(CatalogPlanActions.setSelectionType, (state, { selectionType }) => ({
    ...state,
    selectionType,
  })),
  on(CatalogPlanActions.updateContingentsFilterList, (state, { contingentsFilterList }) => ({
    ...state,
    contingentsFilterList,
  })),
  on(CatalogPlanActions.updateCategoriesFilterList, (state, { categoriesFilterList }) => ({
    ...state,
    categoriesFilterList,
  })),
  on(
    CatalogPlanActions.updateUnavailableSeatTypesFilterList,
    (state, { unavailableSeatTypesFilterList }) => ({
      ...state,
      unavailableSeatTypesFilterList,
    }),
  ),
  on(CatalogPlanActions.updateContingentIdsFilterSelected, (state, { contingentIdsFilterSelected }) => ({
    ...state,
    contingentIdsFilterSelected,
  })),
  on(CatalogPlanActions.updateCategoryIdsFilterSelected, (state, { categoryIdsFilterSelected }) => ({
    ...state,
    categoryIdsFilterSelected,
  })),
  on(
    CatalogPlanActions.updateUnavailableSeatTypeIdsFilterSelected,
    (state, { unavailableSeatTypeIdsSelected }) => ({
      ...state,
      unavailableSeatTypeIdsSelected,
    }),
  ),
  on(CatalogPlanActions.updateSeatStatusIdsFilterSelected, (state, { seatStatusIdsFilterSelected }) => ({
    ...state,
    seatStatusIdsFilterSelected,
  })),
  // Les 2 actions produisent le MEME etat ; seule difference : clearCatalogPlanFilterSelection
  // n'est ecoutee par aucun effect (cf. son commentaire dans les actions).
  on(
    CatalogPlanActions.resetCatalogPlanFilters,
    CatalogPlanActions.clearCatalogPlanFilterSelection,
    (state) => ({
      ...state,
      contingentIdsFilterSelected: [],
      categoryIdsFilterSelected: [],
      unavailableSeatTypeIdsSelected: [],
      seatStatusIdsFilterSelected: [],
      filteredSeatStatusStats: null,
    }),
  ),
  on(CatalogPlanActions.setPlanType, (state, { planType }) => ({
    ...state,
    planType,
  })),
  on(CatalogPlanActions.setBlockViewData, (state, { blockViewData }) => ({
    ...state,
    blockViewData,
  })),
  on(
    CatalogPlanActions.loadBlockViewDataSuccess,
    (state, { eventDetails, blockViewData, seatsStampVersion }) => ({
      ...state,
      eventDetails,
      blockViewData,
      seatSelection: [],
      isPlanLoading: false,
      seatsStampVersion,
    }),
  ),
  on(CatalogPlanActions.loadBlockViewDataFailure, (state) => ({
    ...state,
    blockViewData: null,
    isPlanLoading: false,
  })),
  on(CatalogPlanActions.loadExcludingMultiBlocksPlan, (state) => {
    return {
      ...state,
      isPlanLoading: true,
    };
  }),
  on(
    CatalogPlanActions.loadExcludingMultiBlocksPlanSuccess,
    (state, { eventDetails, blockViewData, seatsStampVersion }) => ({
      ...state,
      eventDetails,
      blockViewData: blockViewData,
      isPlanLoading: false,
      seatsStampVersion,
    }),
  ),
  on(CatalogPlanActions.loadExcludingMultiBlocksPlanFailure, (state) => ({
    ...state,
    blockViewData: null,
    isPlanLoading: false,
  })),
  on(CatalogPlanActions.refreshCatalogBlockViewSeatsDataSuccess, (state, { seatsStampVersion }) => ({
    ...state,
    seatsStampVersion,
    multiQuickFilter: state.bulkSelectionStats
      ? state.multiQuickFilter
      : {
          selectedCategories: null,
          selectedContingents: null,
          selectedUnavailabilities: null,
          selectedStatuses: null,
          filterType: null,
        },
  })),
  on(CatalogPlanActions.refreshCatalogExcludingMultiBlocksSeatsSuccess, (state, { seatsStampVersion }) => ({
    ...state,
    seatsStampVersion,
  })),
  on(CatalogPlanActions.reloadPlanFilterDataSuccess, (state, { planData }) => ({
    ...state,
    planData,
  })),
  on(CatalogPlanActions.updateSeatStatusOptionFilterSelected, (state, { seatStatusOptionSelected }) => ({
    ...state,
    seatStatusOptionFilterSelected: seatStatusOptionSelected,
  })),
  on(CatalogPlanActions.setSeatColorsFilter, (state, { seatColorsFilter }) => ({
    ...state,
    seatColorsFilter,
  })),
  on(CatalogPlanActions.updateAllContingents, (state, { allContingents }) => ({
    ...state,
    allContingents,
  })),
  on(CatalogPlanActions.updateAllCategories, (state, { allCategories }) => ({
    ...state,
    allCategories,
  })),
  on(CatalogPlanActions.updateAllUnavailableSeats, (state, { allUnavailableSeats }) => ({
    ...state,
    allUnavailableSeats,
  })),
  on(CatalogPlanActions.updateSeatSelection, (state, { selection }) => {
    const existingSelection = new Set(state.seatSelection.map((seat) => seat.seatId));
    const newSelection = new Set(selection.map((seat) => seat.seatId));

    const newSeatsToAdd = selection.filter((seat) => !existingSelection.has(seat.seatId));

    const isSelectionEmpty = state.seatSelection.length === 0;
    const isFullSelectionMatch = existingSelection.size > 0 && existingSelection.size === newSelection.size;

    const allExistingSeatsInSelection =
      isFullSelectionMatch && Array.from(newSelection).every((seatId) => existingSelection.has(seatId));

    const noExistingSeatsInSelection =
      isSelectionEmpty && !Array.from(existingSelection).some((seatId) => newSelection.has(seatId));

    if (noExistingSeatsInSelection) {
      return {
        ...state,
        seatSelection: selection,
      };
    }

    if (allExistingSeatsInSelection) {
      return {
        ...state,
        seatSelection: [],
      };
    }

    if (newSeatsToAdd.length > 0) {
      return {
        ...state,
        seatSelection: [...state.seatSelection, ...newSeatsToAdd],
      };
    }

    return {
      ...state,
      seatSelection: state.seatSelection.filter((existingSeat) => !newSelection.has(existingSeat.seatId)),
    };
  }),
  on(CatalogPlanActions.resetSeatSelectionSuccess, (state) => ({
    ...state,
    seatSelection: [],
    selectedSeatChanges: {
      categoryId: null,
      contingentId: null,
      unavailabilityId: null,
    },
  })),
  on(CatalogPlanActions.updateSelectedSeatChanges, (state, { selectedSeatChanges }) => ({
    ...state,
    selectedSeatChanges,
  })),
  on(CatalogPlanActions.updateMultiQuickFilter, (state, { multiQuickFilter }) => ({
    ...state,
    multiQuickFilter,
  })),
  on(CatalogPlanActions.resetMultiQuickFilter, (state) => {
    if (state.bulkSelectionStats) {
      return state;
    }

    return {
      ...state,
      multiQuickFilter: {
        selectedCategories: null,
        selectedContingents: null,
        selectedUnavailabilities: null,
        selectedStatuses: null,
        filterType: null,
      },
    };
  }),
  on(CatalogPlanActions.applyPlanFilterAndSelect, (state, { contingentIds, categoryIds, unavailableSeatTypeIds }) => ({
    ...state,
    multiQuickFilter: {
      selectedContingents: contingentIds.length
        ? state.contingentsFilterList.filter((c) => contingentIds.includes(c.id))
        : null,
      selectedCategories: categoryIds.length
        ? state.categoriesFilterList.filter((c) => categoryIds.includes(c.id))
        : null,
      selectedUnavailabilities: unavailableSeatTypeIds.length
        ? state.unavailableSeatTypesFilterList.filter((u) => unavailableSeatTypeIds.includes(u.id))
        : null,
      selectedStatuses: null,
      filterType: null,
    },
  })),
  on(CatalogPlanActions.updateBlockCategories, (state, { blockCategories }) => ({
    ...state,
    blockCategories,
  })),
  on(CatalogPlanActions.updateBlockContingents, (state, { blockContingents }) => ({
    ...state,
    blockContingents,
  })),
  on(CatalogPlanActions.updateBlockUnavailableSeats, (state, { blockUnavailableSeats }) => ({
    ...state,
    blockUnavailableSeats,
  })),
  on(CatalogPlanActions.updateViewSeatStatusStats, (state, { viewSeatStatusStats }) => ({
    ...state,
    viewSeatStatusStats,
  })),
  on(CatalogPlanActions.updateViewMultiSelectionStats, (state, { viewMultiSelectionStats }) => ({
    ...state,
    viewMultiSelectionStats,
  })),
  on(CatalogPlanActions.updatePlanWideSeatStatusStats, (state, { planWideSeatStatusStats }) => ({
    ...state,
    planWideSeatStatusStats,
  })),
  on(CatalogPlanActions.updateFilteredSeatStatusStats, (state, { filteredSeatStatusStats }) => ({
    ...state,
    filteredSeatStatusStats,
  })),
  on(CatalogPlanActions.updateBulkSelectionStats, (state, { bulkSelectionStats, scope }) => ({
    ...state,
    bulkSelectionStats,
    // Le perimetre suit la selection : fourni a la creation, efface avec elle, conserve tel quel si
    // une mise a jour ne le redefinit pas (l'invariant « scope non nul <=> stats non nulles » tient).
    bulkSelectionScope: bulkSelectionStats ? scope ?? state.bulkSelectionScope : null,
  })),
  on(CatalogPlanActions.resetBulkSelectionStats, (state) => ({
    ...state,
    bulkSelectionStats: null,
    bulkSelectionScope: null,
  })),
  on(CatalogPlanActions.updateBlockScopedFilterStats, (state, { blockScopedFilterStats }) => ({
    ...state,
    blockScopedFilterStats,
  })),
);
